// v0.327.0 (2026-11-04) - THE TEAM PICKER ASKS INSPEC TOO.
// Brett: searching "Add team member" also searches InSpec people, and picking
// one brings them into Contacts and adds them to the team in one press.
//
// The "From InSpec" part of the team picker, under the DropPin matches. It
// reads the same live list as Contacts -> Bring in from InSpec (never kept on
// the device) and names every state: searching, people, nobody, more than
// shown, no seat, not set up, InSpec down, offline. A company that does not run
// InSpec sees nothing here at all.
import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { apiGet } from '../api';
import { theme } from '../theme';
import { listFailure } from './ImportFromInSpecModal';
import { inspecRowAction, inspecSearchable, INSPEC_DEBOUNCE_MS } from '../utils/teamPickerInSpec';

const PATH = '/api/import-inspec-contacts';
const MAX_Q = 100; // the server's MAX_Q

const heading = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
  color: theme.textLight, margin: '10px 0 4px',
};
const quiet = { fontSize: 13, color: theme.textLight, padding: '4px 0' };
const retryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 8,
  border: 0, background: 'transparent', color: theme.brand, cursor: 'pointer',
  fontSize: 12, fontWeight: 700, fontFamily: 'inherit', padding: 0,
};

export default function TeamPickerInSpec({ q, contacts, teamIds, onAddExisting, onImport }) {
  const [view, setView] = useState({ state: 'idle' });
  const [busy, setBusy] = useState(null);     // externalId being added
  const [notes, setNotes] = useState({});     // externalId -> { note, tone }
  const [again, setAgain] = useState(0);
  const seq = useRef(0);
  const words = String(q || '').trim().slice(0, MAX_Q);
  const active = inspecSearchable(words);

  useEffect(() => {
    const my = ++seq.current;
    if (!active) { setView({ state: 'idle' }); return undefined; }
    setView((v) => (v.state === 'list' ? { ...v, searching: true } : { state: 'busy' }));
    const t = setTimeout(async () => {
      try {
        const r = await apiGet(`${PATH}?q=${encodeURIComponent(words)}`);
        if (my !== seq.current) return;
        const people = Array.isArray(r && r.people) ? r.people : null;
        if (!people) { setView({ state: 'error', retry: true }); return; }
        setView({ state: 'list', people, truncated: !!r.truncated });
      } catch (e) {
        if (my !== seq.current) return;
        setView(listFailure(e));
      }
    }, INSPEC_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [words, active, again]);

  if (!active || view.state === 'idle' || view.state === 'ended' || view.state === 'no-inspec') return null;

  async function pick(p, act) {
    if (busy) return;
    setBusy(p.externalId);
    setNotes((n) => ({ ...n, [p.externalId]: undefined }));
    try {
      const out = act.kind === 'existing' ? await onAddExisting(act.contact) : await onImport(p);
      if (out && out.note) setNotes((n) => ({ ...n, [p.externalId]: out }));
    } finally {
      setBusy(null);
    }
  }

  let body;
  if (view.state === 'busy') {
    body = <div style={quiet} data-state="inspec-busy">Searching InSpec…</div>;
  } else if (view.state === 'list') {
    const rows = view.people;
    body = rows.length === 0 ? (
      <div style={quiet} data-state="inspec-none">Nobody in InSpec matches.</div>
    ) : (
      <>
        {rows.map((p) => {
          const act = inspecRowAction(p, contacts, teamIds);
          const isBusy = busy === p.externalId;
          const can = (act.kind === 'import' || act.kind === 'existing') && !busy;
          const note = notes[p.externalId];
          const meta = [p.company, p.email].filter(Boolean).join(' · ');
          const right = act.kind === 'on-team' ? 'On this team'
            : act.kind === 'blocked' ? 'Cannot add'
              : isBusy ? (act.kind === 'existing' ? 'Adding…' : 'Bringing in…') : '+ Add';
          return (
            <div key={p.externalId} data-part="inspec-person" data-action={act.kind}>
              <button
                type="button"
                onClick={() => can && pick(p, act)}
                disabled={!can}
                title={act.kind === 'existing' ? `In your Contacts as ${act.contact.name || 'this person'} - adds them to the team`
                  : act.kind === 'import' ? 'Brings them into your Contacts from InSpec and adds them to the team'
                    : act.why || undefined}
                style={{
                  display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 10, alignItems: 'center',
                  width: '100%', padding: '8px 10px', border: `1px dashed ${theme.border}`, borderRadius: 6,
                  background: theme.surface, cursor: can ? 'pointer' : 'default', textAlign: 'left',
                  fontFamily: 'inherit', opacity: isBusy ? 0.6 : 1,
                }}
              >
                <span style={{ width: 10, height: 10, borderRadius: '50%', border: `2px solid ${theme.textLight}`, boxSizing: 'border-box', display: 'inline-block' }} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{p.name || p.company || 'No name'}</span>
                  {meta && <span style={{ fontSize: 11, color: theme.textLight, marginLeft: 8, overflowWrap: 'anywhere' }}>{meta}</span>}
                  {act.kind === 'existing' && (
                    <span style={{ display: 'block', fontSize: 11, color: theme.textLight }}>In your Contacts as {act.contact.name || 'this person'}</span>
                  )}
                  {act.warn && <span style={{ display: 'block', fontSize: 11, color: theme.warn }}>{act.warn}</span>}
                  {act.kind === 'blocked' && <span style={{ display: 'block', fontSize: 11, color: theme.warn }}>{act.why}</span>}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: can || isBusy ? theme.brand : theme.textLight, whiteSpace: 'nowrap' }}>{right}</span>
              </button>
              {note && (
                <div data-part="inspec-note" data-tone={note.tone} role="status"
                  style={{ fontSize: 12, padding: '4px 10px 2px', color: note.tone === 'error' ? theme.danger : note.tone === 'warn' ? theme.warn : theme.text }}>
                  {note.note}
                </div>
              )}
            </div>
          );
        })}
        {view.truncated && <div style={quiet} data-state="inspec-more">More in InSpec - type more of the name.</div>}
      </>
    );
  } else if (view.state === 'no-seat' || view.state === 'not-configured') {
    const fallback = view.state === 'no-seat'
      ? 'Bringing people in from InSpec needs an InSpec seat in your company.'
      : 'Bringing people in from InSpec is not set up on this server yet.';
    body = <div style={quiet} data-state={`inspec-${view.state}`}>{view.message || fallback}</div>;
  } else {
    const text = {
      offline: 'No connection - InSpec is searched live, so it needs a connection.',
      'signed-out': 'InSpec refused the search: this sign-in is not allowed to read its people.',
      'inspec-down': view.message || 'InSpec did not answer.',
      error: `InSpec could not be searched${view.status ? ` (${view.status})` : ''}.${view.message ? ` ${view.message}` : ''}`,
    }[view.state] || 'InSpec could not be searched.';
    body = (
      <div style={{ ...quiet, color: theme.danger }} data-state={`inspec-${view.state}`}>
        {text}
        <button type="button" style={retryBtn} onClick={() => setAgain((n) => n + 1)}><RefreshCw size={12} /> Try again</button>
      </div>
    );
  }

  return (
    <div data-part="inspec-results" data-state={view.state} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={heading}>From InSpec{view.searching ? ' - searching…' : ''}</div>
      {body}
    </div>
  );
}
